import type { OrderStatus } from "./commerce"

/**
 * Единые подписи статусов заказа (fd-order-01, 31.10.2026).
 *
 * Один статус называется одинаково у покупателя (витрина, «Мои заказы») и у
 * продавца (кабинет, «Продажи»). Действия у сторон разные — покупатель
 * «Подтверждает получение», продавец «Отправляет», — но название статуса
 * одно. До этого словаря витрина писала «Оплачен» / «На обработке» /
 * «Получите», а кабинет про тот же заказ — «В работе» / «Отправлен» /
 * «Доставлен».
 *
 * `new` и `paid` — оба «Оплачен»: `paid` остался от старых снимков, для людей
 * это одно и то же. `pending` и `awaiting_payment` — оба «Ожидает оплаты».
 * `awaiting_payment` — счётчик мобильного макета, в шлюзе его нет, но в
 * перечислении контракта он есть, и подпись у него должна быть.
 *
 * Вкладки кабинета продавца остаются группами («В работе» собирает
 * `new … ready_to_ship`), а чип в строке и в карточке заказа — статус из
 * этого словаря.
 */
export type OrderStatusLocale = "ru" | "en" | "hy"

export const ORDER_STATUS_LABELS: Record<OrderStatus, Record<OrderStatusLocale, string>> = {
  pending: { ru: "Ожидает оплаты", en: "Awaiting payment", hy: "Սպասում է վճարման" },
  awaiting_payment: { ru: "Ожидает оплаты", en: "Awaiting payment", hy: "Սպասում է վճարման" },
  declined: { ru: "Оплата отклонена", en: "Payment declined", hy: "Վճարումը մերժված է" },
  paid: { ru: "Оплачен", en: "Paid", hy: "Վճարված" },
  new: { ru: "Оплачен", en: "Paid", hy: "Վճարված" },
  processing: { ru: "В обработке", en: "Processing", hy: "Մշակվում է" },
  confirmed: { ru: "Подтверждён", en: "Confirmed", hy: "Հաստատված" },
  in_work: { ru: "В работе", en: "In progress", hy: "Ընթացքի մեջ" },
  ready_to_ship: { ru: "Готов к отправке", en: "Ready to ship", hy: "Պատրաստ է առաքման" },
  shipped: { ru: "Отправлен", en: "Shipped", hy: "Ուղարկված" },
  in_transit: { ru: "В пути", en: "In transit", hy: "Ճանապարհին" },
  awaiting_pickup: { ru: "Готов к выдаче", en: "Ready for pickup", hy: "Պատրաստ է ստացման" },
  delivered: { ru: "Доставлен", en: "Delivered", hy: "Առաքված" },
  completed: { ru: "Завершён", en: "Completed", hy: "Ավարտված" },
  cancelled: { ru: "Отменён", en: "Cancelled", hy: "Չեղարկված" },
  return_requested: { ru: "Возврат", en: "Return requested", hy: "Վերադարձ" },
  dispute_open: { ru: "Открыт спор", en: "Dispute open", hy: "Բաց վեճ" },
  archived: { ru: "В архиве", en: "Archived", hy: "Արխիվում" },
}

/** Подпись статуса на языке интерфейса; незнакомый статус — как есть. */
export const orderStatusLabel = (status: string, locale: string = "ru"): string => {
  const labels = ORDER_STATUS_LABELS[status as OrderStatus]
  if (!labels) return status
  return labels[locale.slice(0, 2) as OrderStatusLocale] ?? labels.ru
}
